import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { isInputError } from '../shared/functions';
import { ExpectedOwnedStrategy } from '../shared/owned-strategy-card/strategy';
import { ToastNotificationComponent } from '../shared/toast-notification/toast-notification.component';
import { AuthLinksService } from './auth-links.service';
import { AuthTokenService } from './auth-token.service';
import { LoggerService } from './logger/logger.service';
import { LogoutService } from './logout.service';
import { RedirectPreloaderService } from './redirect-preloader.service';
import { AccessToken, ApiColCollaboratorOut } from './rest-api/rest-api';
import { RestApiService } from './rest-api/rest-api.service';
import { ToastNotificationService } from './toast-notification/toast-notification.service';
import { UserService } from './user/user.service';
import * as Sentry from '@sentry/angular';
import { parseToError } from '../shared/functions-angular';

@Injectable({
  providedIn: 'root'
})
export class LoginService {

  public constructor(
    private restApiService: RestApiService,
    private toastService: ToastNotificationService,
    private redirectPreloaderService: RedirectPreloaderService,
    private authTokenService: AuthTokenService,
    private logoutService: LogoutService,
    private loggerService: LoggerService,
    private authLinksService: AuthLinksService,
    private userService: UserService,
    private router: Router,
  ) {}

  public logIn(bcasKey: string): void {
    this.redirectPreloaderService.redirecting.preloader = true;

    this.userService.removeUserFromLocal();
    this.restApiService.backendLoginConsult(bcasKey, this.authLinksService.consultRedirectUri).subscribe({
      next: (result) => {
        this.consultBackendSuccesfullyAuthorized(result);
      },
      error: (error) => this.backendError(error),
    });
  }

  private consultBackendSuccesfullyAuthorized(data: unknown): void {
    if (this.validateApiResponse(data)) {
      this.authTokenService.updateBackendTokens(data as AccessToken);
      this.fetchConsultData();
    } else {
      this.backendError();
    }
  }

  private validateApiResponse(data: unknown): data is AccessToken {
    if (typeof data === 'object' &&
      data !== null &&
      'access_token' in data && typeof data.access_token === 'string' &&
      'expires_in' in data && typeof data.expires_in === 'number' &&
      'refresh_token' in data && typeof data.refresh_token === 'string' &&
      'token_type' in data && typeof data.token_type === 'string'
    ) {
        return true;
    }

    this.toastService.addNotification({
      title: 'toastNotifications.headers.authError',
      message: 'toastNotifications.messages.serverAuthError',
      type: 'warning',
      component: ToastNotificationComponent
    });

    return false;
  }

  public fetchConsultData(): void {
    this.restApiService.getConsultDetail().subscribe({
      next: this.updateConsultAndNavigate.bind(this),
      error: this.logRequestError.bind(this),
    });
  }

  private updateConsultAndNavigate(data: ApiColCollaboratorOut): void {
    if(data.collaboratorId) {
      this.userService.loggedConsult = data;
    } else {
      Sentry.addBreadcrumb({ data });
      this.logRequestError('Failed to validate collaborator data response.');
    }

    this.redirectPreloaderService.redirecting.preloader = false;
  }

  private handlePortfoliosAndStrategiesData(data: ExpectedOwnedStrategy[]) {
    this.userService.hasAnyPortfolioOrStrategy = !!data?.length;
  }

  private logRequestError(error?: unknown): void {
    this.loggerService.error(typeof error === 'function' ? error() : error);
  }

  private backendError(error?: unknown): void {
    const apiErrorCodes = [
      'E001', // auth successfull but without rights
      'E016', // not auth by BCAS
      'E017', // not in db/wrong gained oauth data
    ];
    const apiStatusCodes = [
      401,
      422,
    ];

    const parsedError = parseToError(error);

    if(parsedError &&
      apiStatusCodes.includes(parsedError.status) &&
      apiErrorCodes.includes(parsedError.error.errorCode)
    ) {
      if(parsedError.error.idToken) {
        this.logoutService.finishLogout({
          idToken: parsedError.error.idToken,
          displayErrorOnRedirectBack: 'consult'
        });
      } else {
        this.logoutService.finishLogout({displayErrorOnRedirectBack: 'consult'});
      }
    } else {
      this.toastService.addNotification({
        title: 'toastNotifications.headers.authError',
        message: 'toastNotifications.messages.authError',
        type: 'warning',
        component: ToastNotificationComponent
      });
      this.router.navigate(['/login']);
    }

    if(isInputError(error, 401)) {
        this.logoutService.finishLogout({displayErrorOnRedirectBack: 'consult'});
      }

    this.redirectPreloaderService.redirecting.preloader = false;
  };
}
